/**
 * Which PRODUCT this build is — Thunder, or a white-labelled product built from the same code.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY A BUILD-TIME PRODUCT, WHEN THE ORGANISATION ALREADY OWNS ITS THEME
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * The organisation's palette and accent (migration 0058) are applied from `/me`, which means
 * they exist only AFTER sign-in. Everything a white-label client judges the product by first is
 * painted BEFORE it: the browser tab's title and icon, the sign-in page, the splash. Cratus asked
 * for its own branding to be prominent, and a sign-in page that says "Thunder" until the password
 * is accepted is not that. So the product is a property of the BUILD — one bundle per product,
 * exactly as one bundle per environment already is (the Dockerfile bakes every `VITE_*` in).
 *
 * The two mechanisms do not overlap: the product owns name, artwork, sign-in and which modules
 * exist; the organisation keeps owning palette and accent. See docs/CRATUS.md.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THIS FILE IMPORTS NOTHING
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * `vite.config.ts` imports it to rewrite `index.html` at build time, and the config is bundled
 * by esbuild for Node: no `import.meta.env`, no `@/` alias, no React, no DOM. Keep it that way —
 * the app-facing half of the profile is `profile.ts`.
 */

export type ProductId = 'thunder' | 'cratus';

export const PRODUCT_IDS: readonly ProductId[] = ['thunder', 'cratus'];

/**
 * THE PRODUCT THIS BRANCH BUILDS WHEN NOTHING SAYS OTHERWISE — and the one line of this seam that
 * differs between branches. `dev` builds Thunder; the `cratus` branch builds Cratus, so a
 * developer who checks it out and runs `npm run dev` sees the product the branch exists for.
 * `VITE_PRODUCT=thunder` still builds Thunder from here, which is how to check that a change made
 * for Cratus left Thunder alone.
 */
export const DEFAULT_PRODUCT: ProductId = 'cratus';

/**
 * `VITE_PRODUCT` → a product id. Empty or absent means the branch default.
 *
 * An UNKNOWN value throws rather than falling back. A white-label build that quietly ships as
 * the default product — because someone typed `cratos` into a pipeline variable — is the one
 * failure this seam exists to prevent, and the build is the cheapest place to stop it.
 */
export function resolveProductId(raw: string | undefined): ProductId {
  const value = raw?.trim().toLowerCase() ?? '';
  if (value === '') return DEFAULT_PRODUCT;
  if ((PRODUCT_IDS as readonly string[]).includes(value)) return value as ProductId;
  throw new Error(
    `VITE_PRODUCT="${raw}" is not a product this code builds. Expected one of: ${PRODUCT_IDS.join(', ')}.`,
  );
}

/** What `index.html` says about the product, before any script has run. */
export interface ProductDocument {
  title: string;
  description: string;
  /** Root-relative paths into `public/`, in the three slots `index.html` explains. */
  icons: { svg: string; png32: string; png180: string };
  /**
   * The colour mode for somebody who has never chosen one. `null` follows the operating system,
   * which is what Thunder does; Cratus is designed dark-first, so it opens dark until the person
   * picks otherwise from the theme menu.
   */
  defaultTheme: 'dark' | 'light' | null;
  /**
   * Whether panels are translucent with a blurred backdrop, for somebody who has never
   * chosen. `null` means the product has no glass at all — it offers no control and
   * `tokens.css` carries no block it could match.
   *
   * Thunder is `null` on purpose and not `'off'`: 'off' would imply there is an 'on' to
   * reach, and there is not. Its surfaces were made opaque for measured legibility reasons
   * recorded at length in `tokens.css`, and nothing here reopens that.
   */
  defaultGlass: 'on' | 'off' | null;
  /**
   * The Google Fonts stylesheet this product's `--font-sans` actually needs.
   *
   * A product can name a typeface in `tokens.css` all it likes; if `index.html` does not
   * fetch it the console silently renders the next entry in the stack, which on macOS is a
   * system face close enough to pass a glance and wrong in every measurement. So the
   * declaration and the fetch are one decision, made here.
   *
   * Both products keep Geist Mono: `--font-mono` still sets `code`, `kbd`, `samp` and the
   * SLD's micro-labels in both, even though Cratus's numbers moved to the body face.
   */
  fontHref: string;
  /**
   * Which product this build IS, written onto the `<html>` tag by `index-html.ts`.
   *
   * It is the fourth theming axis and the only one that is not a choice — see
   * `:root[data-product='cratus']` in `tokens.css`. Static rather than script-written
   * because it is known at build time: it then survives a thrown pre-paint script, costs
   * nothing at first paint, and cannot disagree with the bundle it ships in.
   */
  product: ProductId;
  /** The animated mark the page shows while it loads, from `LOADER_MARK`. */
  loaderMark: string;
}

/**
 * THE LOADER: EACH PRODUCT'S OWN MARK, DRAWN, FILLED, HELD AND FADED, ON A LOOP.
 *
 * One string per product, used in two places that must look identical, because one hands over
 * to the other mid-load:
 *
 *   · `index.html`, as the splash inside `#root`. It paints before any script has run, and React
 *     replaces it as it mounts. Thunder's copy is the file's own text, so a Thunder build is still
 *     rewritten to itself (`index-html.test.ts` holds the two equal). Cratus's is swapped in by
 *     `rewriteIndexHtml`.
 *   · `components/lumen/BrandLoader.tsx`, for every loading state after that.
 *
 * The motion is CSS in `index.html`'s `#lm-loader-css`, also loaded before any script, so the
 * splash animates from the first frame. Classes: `lm-mark` on the svg, and each path draws its
 * outline (`pathLength="1"` makes the dash maths size-free), then fills. `lm-mark-disc` grows
 * Thunder's disc behind its bolt. `lm-mark-grad` is the one id, made unique per instance by
 * `BrandLoader`, so two loaders on a page never share a gradient. The svg's `color` is the glow:
 * the product's accent, so the mark lights in its own colour on either theme.
 *
 * The Cratus path data is `assets/brand/cratus-mark.svg`'s, and Thunder's is
 * `thunder-mark-circle.svg`'s. Replace both when the official artwork arrives (`CRATUS.md` §3).
 */
export const LOADER_MARK: Record<ProductId, string> = {
  thunder:
    '<svg class="lm-mark" style="color:#FF8A00" viewBox="0 0 96 96" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
    '<defs><linearGradient id="lm-mark-grad" x1="29.5" y1="97" x2="85.6" y2="43.5" gradientUnits="userSpaceOnUse">' +
    '<stop stop-color="#FF7F02"/><stop offset="1" stop-color="#FFC400"/></linearGradient></defs>' +
    '<rect class="lm-mark-disc" width="96" height="96" rx="48" fill="url(#lm-mark-grad)"/>' +
    '<path pathLength="1" fill="#2C2C2C" stroke="#2C2C2C" stroke-width="1.5" stroke-linejoin="round" ' +
    'd="M73.4 18.65C72.88 17.38 71.86 16.83 70.51 17.05C68.82 17.32 67.45 18.27 66.13 19.29C65.16 20.03 64.27 20.88 63.33 21.66C63.19 21.79 63.01 21.95 62.84 21.98C61.74 22.2 60.65 22.39 59.53 22.54C61 21.11 62.59 19.87 64.53 19.14C64.51 19.07 64.51 19 64.5 18.93C63.79 18.97 63.06 18.91 62.37 19.07C59.99 19.58 58.01 20.91 56.17 22.42C55.07 23.32 22 49.34 22 49.34H40.63L22.04 78L70.61 43.53H56.99C61.06 37.47 68.98 25.65 70.54 23.98C70.81 23.69 71.13 23.41 71.45 23.18C72.55 22.41 73.12 22.49 73.95 23.59C74.05 21.85 74.06 20.22 73.41 18.65Z"/>' +
    '</svg>',
  cratus:
    '<svg class="lm-mark" style="color:#C23B3B" viewBox="-1 -1 92 102" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
    '<g fill="#C23B3B" stroke="#C23B3B" stroke-width="1.6" stroke-linejoin="round">' +
    '<path pathLength="1" fill-rule="evenodd" d="M0 0L90 52.3V74.3L45.1 99.4L0 74.3V54.3L20 41.4L0 32ZM22 63.7L45.1 51.4L67.1 63.7L45.1 76.3Z"/>' +
    '<path class="lm-mark-late" pathLength="1" d="M30.4 12.2C50 10.4 70 9.4 78 6.6C83 4.9 87 2.8 90 0.9V33.7C87.6 32.3 85.6 30.8 83.4 30.3C80.6 29.8 78.5 31.8 76 32.8C73.5 33.8 71.5 34.5 69.5 34.2C67.5 33.9 66 33.2 64.3 32.6Z"/>' +
    '</g></svg>',
};

export const PRODUCT_DOCUMENT: Record<ProductId, ProductDocument> = {
  /* Thunder's values are exactly what `index.html` already carries, so a Thunder build is
     rewritten to itself and its output is byte-identical to `dev`. */
  thunder: {
    title: 'Thunder — Remote Monitoring & Compliance',
    description:
      'Model, evaluate and certify critical-facility power chains against 21 international standards.',
    icons: { svg: '/favicon.svg', png32: '/favicon-32.png', png180: '/favicon-180.png' },
    defaultTheme: null,
    defaultGlass: null,
    product: 'thunder',
    fontHref:
      'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Geist+Mono:wght@400;500;600;700&display=swap',
    loaderMark: LOADER_MARK.thunder,
  },
  cratus: {
    title: 'Cratus — Smart Security Suite',
    description:
      'Security, power and asset monitoring for the cabinets and sites of critical infrastructure.',
    icons: {
      svg: '/cratus/favicon.svg',
      png32: '/cratus/favicon-32.png',
      png180: '/cratus/favicon-180.png',
    },
    defaultTheme: 'dark',
    defaultGlass: 'on',
    product: 'cratus',
    fontHref:
      'https://fonts.googleapis.com/css2?family=Bai+Jamjuree:wght@300;400;500;600;700&family=Geist+Mono:wght@400;500;600;700&display=swap',
    loaderMark: LOADER_MARK.cratus,
  },
};
